import { z } from "zod";

export const ClientSchema = z.object({
  id: z.string(),
  name: z.string(),
  color: z.string(),
  weeklyTargetDays: z.number().optional(),
  pickerOrder: z.number().optional(),
  archivedAt: z.number().optional(),
});

export const TimeEntrySchema = z.object({
  id: z.string(),
  description: z.string(),
  startTime: z.number(),
  endTime: z.number().optional(),
  clientId: z.string().optional(),
});

export const SmoothingSettingsSchema = z.object({
  enabled: z.boolean(),
  thresholdMinutes: z.number(),
});

// How many recent qualifying working days the Rhythm panel's Typical column
// pulls from. Wider = steadier median, slower to adapt to habit changes;
// narrower = more responsive but noisier. A fixed preset set — the panel
// exposes them as chips, no free-form number, so the union is the contract.
// 260 ≈ a working year (5 days/week × 52 weeks).
export const RhythmTypicalLookbackSchema = z.union([
  z.literal(5),
  z.literal(10),
  z.literal(20),
  z.literal(40),
  z.literal(60),
  z.literal(180),
  z.literal(260),
]);

// How many recent Monday-weeks the Work-weeks panel spans. Fixed preset set,
// exposed as chips; the union is the contract. Added in v8 → v9.
export const CadenceLookbackWeeksSchema = z.union([
  z.literal(4),
  z.literal(8),
  z.literal(12),
  z.literal(26),
  z.literal(52),
]);

export const SettingsSchema = z.object({
  dayHours: z.number(),
  workdaysPerWeek: z.union([
    z.literal(4),
    z.literal(5),
    z.literal(6),
    z.literal(7),
  ]),
  smoothing: SmoothingSettingsSchema,
  rhythmTypicalLookback: RhythmTypicalLookbackSchema,
  cadenceLookbackWeeks: CadenceLookbackWeeksSchema,
});

export const PersistedStateSchema = z.object({
  timeEntries: z.record(z.string(), TimeEntrySchema),
  clients: z.record(z.string(), ClientSchema),
  settings: SettingsSchema,
});

export type PersistedState = z.infer<typeof PersistedStateSchema>;

// Shape of a JSON export file produced by exportTimeEntries("json"). Used at
// the import boundary to validate user-supplied files before they reach the
// store. version: 1 corresponds to the { clients, entries } shape; older
// shapes (flat Entry[]) are not supported by import.
export const ImportFileSchema = z.object({
  version: z.literal(1),
  exportedAt: z.number(),
  clients: z.array(ClientSchema),
  entries: z.array(TimeEntrySchema),
});

export type ImportFile = z.infer<typeof ImportFileSchema>;
